import * as THREE from "three";
import { createSprite, type Sprite } from "./sprite.ts";
import { clampInsideHex, HEX_APOTHEM } from "./hex.ts";
import {
  ACTIVE_CANDIDATE_SLUGS,
  candidateColor,
  displayName,
  type Slug,
} from "../../lib/candidates.ts";
import { COMIC_FONT } from "./text-panel.ts";

// Party-people sprites (billboarded line-art) that roam the room and bob gently.
// Some carry a "Voto por ___" sign for a random candidate that changes over time.
const PEOPLE = [
  "/assets/party-meme-character.png",
  "/assets/party-meme-character-2.png",
  "/assets/party-meme-character-female.png",
  "/assets/party-meme-guy-dancing.png",
  "/assets/party-meme-lady-dancing.png",
  "/assets/party-conversation-1.png",
  "/assets/party-conversation-2.png",
];

const MARGIN = 3; // keep the crowd off the walls/booths
const BASE_Y = 0.95; // sprite centre so feet ~rest on the floor
const SIGN_CHANCE = 0.2; // share of the crowd holding a candidate sign
const SWAP_EVERY = 3.5; // seconds between one carrier switching candidate
const rand = (a: number, b: number) => a + Math.random() * (b - a);
const pick = <T>(xs: readonly T[]): T =>
  xs[Math.floor(Math.random() * xs.length)];

function randomSpot(): { x: number; z: number } {
  const r = Math.sqrt(Math.random()) * (HEX_APOTHEM - MARGIN);
  const a = Math.random() * Math.PI * 2;
  return clampInsideHex(Math.cos(a) * r, Math.sin(a) * r, MARGIN);
}

const rgba = (hex: string, a: number): string => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// A little "🪧 Voto por <name>" placard, white paper washed subtly in the
// candidate's colour, drawn in the wojak comic font. Seen front-on (not
// mirrored — unlike the player's own held sign).
function signTexture(slug: Slug): THREE.CanvasTexture {
  const W = 512;
  const H = 224;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d")!;

  roundRect(ctx, 8, 8, W - 16, H - 16, 26);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.fillStyle = rgba(candidateColor(slug), 0.16); // subtle colour wash
  ctx.fill();
  ctx.lineWidth = 7;
  ctx.strokeStyle = "#000";
  ctx.stroke();

  ctx.fillStyle = "#000";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `bold 34px ${COMIC_FONT}`;
  ctx.fillText("🪧 Voto por", W / 2, 72);

  const name = displayName(slug);
  let fs = 58;
  do {
    ctx.font = `bold ${fs}px ${COMIC_FONT}`;
    if (ctx.measureText(name).width <= W - 56) break;
    fs -= 3;
  } while (fs > 24);
  ctx.fillText(name, W / 2, 152);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

interface Sign {
  mesh: THREE.Mesh;
  set(slug: Slug): void;
}

function createSign(slug: Slug): Sign {
  const mat = new THREE.MeshBasicMaterial({
    map: signTexture(slug),
    transparent: true,
    depthWrite: false,
    toneMapped: false,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1.9, 0.83), mat);
  mesh.userData.skipEdges = true; // line pass ignores it
  mesh.renderOrder = 2; // over the crowd sprites
  return {
    mesh,
    set(s) {
      mat.map?.dispose();
      mat.map = signTexture(s);
      mat.needsUpdate = true;
    },
  };
}

interface Npc {
  sprite: Sprite;
  bobPhase: number;
  bobSpeed: number;
  tx: number;
  tz: number;
  speed: number;
  sign?: Sign;
}

export interface Crowd {
  group: THREE.Group;
  update(dt: number, camera: THREE.Camera): void;
}

export function createCrowd(count = 24): Crowd {
  const group = new THREE.Group();
  group.name = "crowd";
  const npcs: Npc[] = [];

  for (let i = 0; i < count; i++) {
    const sprite = createSprite(PEOPLE[i % PEOPLE.length], rand(1.7, 2.1));
    const spot = randomSpot();
    sprite.mesh.position.set(spot.x, BASE_Y, spot.z);
    group.add(sprite.mesh);

    let sign: Sign | undefined;
    if (Math.random() < SIGN_CHANCE) {
      sign = createSign(pick(ACTIVE_CANDIDATE_SLUGS));
      group.add(sign.mesh);
    }

    const t = randomSpot();
    npcs.push({
      sprite,
      bobPhase: rand(0, Math.PI * 2),
      bobSpeed: rand(1.6, 3),
      tx: t.x,
      tz: t.z,
      speed: rand(0.25, 0.6), // slow wander
      sign,
    });
  }

  const carriers = npcs.filter((n) => n.sign);
  let elapsed = 0;
  let sinceSwap = 0;
  return {
    group,
    update(dt, camera) {
      elapsed += dt;
      for (const n of npcs) {
        const m = n.sprite.mesh;
        const dx = n.tx - m.position.x;
        const dz = n.tz - m.position.z;
        const dist = Math.hypot(dx, dz);
        if (dist < 0.4) {
          const t = randomSpot(); // reached → pick a new spot to drift to
          n.tx = t.x;
          n.tz = t.z;
        } else {
          const stepLen = Math.min(n.speed * dt, dist);
          const c = clampInsideHex(
            m.position.x + (dx / dist) * stepLen,
            m.position.z + (dz / dist) * stepLen,
            MARGIN,
          );
          m.position.x = c.x;
          m.position.z = c.z;
        }
        m.position.y = BASE_Y +
          Math.sin(elapsed * n.bobSpeed + n.bobPhase) * 0.06;
        // billboard: face the camera, yaw only (stay upright)
        m.rotation.y = Math.atan2(
          camera.position.x - m.position.x,
          camera.position.z - m.position.z,
        );
        // carry the sign above the head, facing the same way
        if (n.sign) {
          n.sign.mesh.position.set(
            m.position.x,
            m.position.y + 1.55,
            m.position.z,
          );
          n.sign.mesh.rotation.y = m.rotation.y;
        }
      }

      // every so often, one sign-carrier changes their mind
      sinceSwap += dt;
      if (sinceSwap >= SWAP_EVERY && carriers.length) {
        sinceSwap = 0;
        pick(carriers).sign!.set(pick(ACTIVE_CANDIDATE_SLUGS));
      }
    },
  };
}
